import { useEffect, useState } from "react";
import { answerText, isAnswered, OTHER_KEY, questionsOf } from "../../shared/questions";
import type { ApprovalDTO, ApprovalQuestion, QuestionAnswer, QuestionOption } from "../../shared/types";
import { ApiError, get, post } from "../lib/api";
import { Button, ErrorText, inputCls } from "./ui";
import { AddressedTo, Decided } from "./Verdicts";

/** Saves one question's answer. It throws so the question that asked can say why. */
type Submit = (questionId: string, answer: QuestionAnswer) => Promise<void>;

const optionCls =
  "rounded-xl px-3.5 py-2.5 text-left text-sm ring-1 ring-line transition hover:bg-fill-subtle disabled:pointer-events-none disabled:opacity-40";
const pickedCls = "bg-haze-soft ring-haze-deep dark:bg-haze/10 dark:ring-haze/40";

/** "Other" is implied on every choice question and is never one of the agent's own options. */
const OTHER_OPTION: QuestionOption = { key: OTHER_KEY, label: "Other" };

function OptionLabel({ o }: { o: QuestionOption }) {
  return (
    <span className="min-w-0">
      <span className="block font-medium">{o.label}</span>
      {o.description && <span className="mt-0.5 block text-muted">{o.description}</span>}
    </span>
  );
}

/** The field "Other" opens, and the one a free-text question gets. Only a field the person opened takes the focus. */
function TypedAnswer({ value, onChange, onSend, busy, placeholder, focus = false }: { value: string; onChange: (v: string) => void; onSend: () => void; busy: boolean; placeholder: string; focus?: boolean }) {
  return (
    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
      <input
        className={`${inputCls} sm:flex-1`}
        autoFocus={focus}
        placeholder={placeholder}
        value={value}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            e.preventDefault();
            onSend();
          }
        }}
      />
      <Button variant="primary" className="shrink-0 self-start sm:self-center" disabled={busy || !value.trim()} onClick={onSend}>
        Send
      </Button>
    </div>
  );
}

/** One click answers and locks; "Other" opens a field instead. `locked` draws the choices for a reader who cannot answer. */
function SingleChoice({ q, submit, locked = false }: { q: ApprovalQuestion; submit: Submit; locked?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [other, setOther] = useState<string | null>(null);
  const options = [...(q.options ?? []), OTHER_OPTION];
  // Descriptions need a line of their own, so those options stack instead of sitting in a row.
  const roomy = options.some((o) => o.description);

  const send = async (answer: QuestionAnswer) => {
    setBusy(true);
    setError(null);
    try {
      await submit(q.id, answer);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3">
      <div className={roomy ? "space-y-2" : "flex flex-wrap gap-2"}>
        {options.map((o) => {
          const isOther = o.key === OTHER_KEY;
          return (
            <button
              key={o.key}
              type="button"
              disabled={busy || locked}
              onClick={() => (isOther ? setOther("") : send({ keys: [o.key] }))}
              className={`${optionCls} ${roomy ? "block w-full" : ""} ${isOther && other !== null ? pickedCls : ""}`}
            >
              <OptionLabel o={o} />
            </button>
          );
        })}
      </div>
      {other !== null && (
        <TypedAnswer value={other} onChange={setOther} busy={busy} focus placeholder="Type your answer" onSend={() => send({ keys: [OTHER_KEY], other: other.trim() })} />
      )}
      {error && <div className="mt-2"><ErrorText error={error} /></div>}
    </div>
  );
}

/** Checkboxes: nothing is sent until Submit. */
function MultiChoice({ q, submit, locked = false }: { q: ApprovalQuestion; submit: Submit; locked?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keys, setKeys] = useState<string[]>([]);
  const [other, setOther] = useState("");
  const options = [...(q.options ?? []), OTHER_OPTION];
  const picked = keys.includes(OTHER_KEY);
  const ready = keys.length > 0 && (!picked || Boolean(other.trim()));

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await submit(q.id, { keys, other: picked ? other.trim() : null });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {options.map((o) => (
        <label key={o.key} className={`flex items-start gap-3 ${optionCls} ${keys.includes(o.key) ? pickedCls : ""}`}>
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-ink dark:accent-haze"
            checked={keys.includes(o.key)}
            disabled={busy || locked}
            onChange={(e) => setKeys((cur) => (e.target.checked ? [...cur, o.key] : cur.filter((k) => k !== o.key)))}
          />
          <OptionLabel o={o} />
        </label>
      ))}
      {picked && (
        <input className={inputCls} autoFocus placeholder="Type your answer" value={other} disabled={busy} onChange={(e) => setOther(e.target.value)} />
      )}
      <div className="pt-1">
        <Button variant="primary" disabled={busy || locked || !ready} onClick={send}>
          Submit answers
        </Button>
      </div>
      <ErrorText error={error} />
    </div>
  );
}

/** No options: the one question the next chat message answers says so; every other one is typed in here. */
function FreeText({ q, canTypeHere, submit }: { q: ApprovalQuestion; canTypeHere: boolean; submit: Submit }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");

  if (!canTypeHere) return <p className="mt-2 text-sm text-muted">Waiting for your reply — your next message answers this.</p>;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await submit(q.id, { text: text.trim() });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1">
      <TypedAnswer value={text} onChange={setText} busy={busy} placeholder="Type your answer" onSend={send} />
      {error && <div className="mt-2"><ErrorText error={error} /></div>}
    </div>
  );
}

function Answered({ text }: { text: string }) {
  return (
    <div className="mt-2.5 flex items-start gap-2 rounded-xl bg-fill-subtle px-3.5 py-2.5 text-sm">
      <span className="shrink-0 text-faint">✓</span>
      <span className="min-w-0 break-words whitespace-pre-wrap text-muted">{text}</span>
    </div>
  );
}

/**
 * One request from an agent: its questions one under the other, each answered and locked on its own. The same card
 * renders in the chat, in every other transcript and on the Requests page. `canTypeHere` gives every free-text
 * question a field and a Send; it is off only on the one card in the chat whose question the person's next typed
 * message really does answer, which is the only place that says so. Only the person it is asked of answers it (D14):
 * anyone else, an admin included, reads the choices drawn off and who it is addressed to.
 */
export function RequestCard({
  approval,
  canAnswer,
  canTypeHere = false,
  onAnswered,
}: {
  approval: ApprovalDTO;
  canAnswer: boolean;
  canTypeHere?: boolean;
  onAnswered?: (updated: ApprovalDTO) => void;
}) {
  // What we just posted holds until the server's own copy arrives (the websocket, or the next poll).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;
  const questions = questionsOf(a);
  const pending = a.status === "pending";

  /** A 409 means it is no longer pending, so the card reloads instead of looking broken. */
  const submit: Submit = async (questionId, answer) => {
    const apply = (updated: ApprovalDTO) => {
      setLocal(updated);
      onAnswered?.(updated);
    };
    try {
      apply(await post<ApprovalDTO>(`/api/approvals/${a.id}/answer`, { answers: { [questionId]: answer } }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) return apply(await get<ApprovalDTO>(`/api/approvals/${a.id}`));
      throw err;
    }
  };

  return (
    <div className="card p-5 md:p-6">
      <div>
        {questions.map((q) => {
          const given = a.answers?.[q.id];
          return (
            <div key={q.id} className="border-t border-line py-5 first:border-t-0 first:pt-0 last:pb-0">
              {q.header && <div className="mb-1 text-tiny font-medium tracking-wide text-faint uppercase">{q.header}</div>}
              <p className="text-reading leading-6 font-medium tracking-tight break-words whitespace-pre-wrap">{q.question}</p>
              {isAnswered(given) ? (
                <Answered text={answerText(q, given)} />
              ) : !pending ? null : q.options?.length ? (
                q.multiSelect ? <MultiChoice q={q} submit={submit} locked={!canAnswer} /> : <SingleChoice q={q} submit={submit} locked={!canAnswer} />
              ) : canAnswer ? (
                <FreeText q={q} canTypeHere={canTypeHere} submit={submit} />
              ) : null}
            </div>
          );
        })}
      </div>
      {pending && !canAnswer && (
        <div className="mt-4">
          <AddressedTo a={a} suffix=", who answers it" />
        </div>
      )}
      {/* Taken back by the agent, or closed because the person it was asked of left. */}
      {a.status === "withdrawn" && <Decided a={a} />}
    </div>
  );
}
